import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { firebaseAuth } from '@/core/auth/firebase';
import { friendlyAuthError } from '@/core/auth/errors';
import { useAuth } from '@/core/auth/useAuth';
import { Button, Logo, ThemeToggle } from '@/shared/ui';
import { pickWallpaper } from './wallpapers';

/**
 * Shared input styling. Declared once so the two fields can never drift apart,
 * and so the focus treatment stays identical between them.
 */
// `text-base` en móvil, los 15px de diseño a partir de `sm`. No es una
// preferencia tipográfica: iOS hace zoom automático al enfocar un campo cuya
// letra mida menos de 16px, y al salir deja la página **desplazada y más
// ancha** — el scroll horizontal aparece después, ya dentro del panel, así que
// no se relaciona con el login. 15px estaba justo por debajo del umbral.
//
// `Input.tsx` lleva este mismo arreglo desde hace tiempo; esta pantalla se lo
// perdió por tener su propia copia del estilo en vez de usar el primitivo.
const FIELD =
  'mt-2 block w-full rounded-full border border-line-strong bg-surface-raised px-5 py-3 text-base sm:text-[0.9375rem] text-content shadow-sm transition-shadow placeholder:text-content-subtle hover:border-content-subtle focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15';

/**
 * Split login: field photography on the left, form on the right.
 *
 * The art is the product's own subject matter — the physical network these
 * crews are dispatched to maintain — so it earns its place instead of being
 * generic decoration. It is deliberately confined to its own panel: the form
 * sits on flat white, where contrast is predictable and nothing competes with
 * the two fields that matter. Below `lg` the photo drops away entirely rather
 * than sliding behind the inputs.
 */
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { firebaseUser, user, loading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Chosen once per mount: re-rolling on every render would swap the image
  // mid-typing.
  const wallpaper = useMemo(() => pickWallpaper(), []);

  // If we land on /login already authenticated, bounce home immediately.
  // RoleRedirect at "/" will dispatch to /admin or /field.
  useEffect(() => {
    if (!authLoading && firebaseUser && user) {
      const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
      navigate(from && from !== '/login' ? from : '/', { replace: true });
    }
  }, [authLoading, firebaseUser, user, navigate, location.state]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signInWithEmailAndPassword(firebaseAuth, email, password);
      // AuthContext picks up the change and hydrates the User from /users/me.
      // The useEffect above will then redirect us. We also push to "/" right
      // away to feel snappy — RoleRedirect will wait for the hydration.
      navigate('/', { replace: true });
    } catch (err) {
      setError(friendlyAuthError(err));
      setLoading(false);
    }
  }

  async function onReset() {
    setError(null);
    setResetSent(false);
    if (!email.trim()) {
      setError('Escribe tu email primero.');
      return;
    }
    try {
      await sendPasswordResetEmail(firebaseAuth, email.trim());
      setResetSent(true);
    } catch (err) {
      setError(friendlyAuthError(err));
    }
  }

  // ── Diagnostic banner ──────────────────────────────────────────────────
  // If we ARE logged in to Firebase but the backend rejected us (user=null
  // after loading), surface a clear message — usually means the Mongo User
  // doesn't exist yet (forgot to run `make seed`).
  const showProvisioningError = !authLoading && firebaseUser && !user && !loading;

  return (
    // min-h-dvh, not min-h-full: `min-height:100%` resolves against the parent,
    // and App's wrapper is an auto-height flex column — so it collapsed to the
    // content and left the page background showing below the panels. The dynamic
    // viewport unit also handles the mobile address bar.
    <div className="grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      {/* ── Art panel ──────────────────────────────────────────────────── */}
      {/* The wallpaper is a CSS background, not an <img>: a hidden <img> is
          still fetched by the browser, which would cost a field operator a
          multi-megabyte download on mobile for art they never see. A
          background on a `display:none` element is never requested. */}
      <section
        className="relative isolate hidden overflow-hidden bg-slate-950 bg-cover bg-center lg:block"
        style={{ backgroundImage: `url(${wallpaper})` }}
      >
        {/* A slight wash keeps the photo from reading brighter than the white
            form panel beside it. Nothing sits on top, so no contrast floor to
            protect — the image is left to be the image. */}
        <div className="absolute inset-0 bg-slate-950/15 dark:bg-slate-950/40" />
        {/* A hairline of light along the seam, so the photo meets the white
            panel as a deliberate edge instead of an abrupt cut. */}
        <div className="absolute inset-y-0 right-0 w-px bg-white/20" />
      </section>

      {/* ── Form panel ─────────────────────────────────────────────────── */}
      {/* A column, so the footer can sit at the bottom edge while the form
          stays optically centred in the space above it. */}
      <section className="flex flex-col bg-surface-raised px-6 py-8">
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm">
            {/* The logo lives here at every size now: on the photo it floated
                over the technician with nothing to align to. Above the heading
                it has an edge to sit on and reads as the product's signature. */}
            <Logo className="mb-7 h-12" alt="" />

            <h1 className="text-[2rem] font-semibold leading-tight tracking-tight text-content">
              Inicia sesión
            </h1>
            <p className="mt-2 text-[0.9375rem] text-content-subtle">
              Entra con la cuenta de tu compañía.
            </p>

            {showProvisioningError && (
              <div className="mt-6 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200">
                <p className="font-semibold">
                  Sesión iniciada, pero el usuario no está provisionado.
                </p>
                <p className="mt-1">
                  El backend no encuentra <code>{firebaseUser.email}</code> en Mongo.
                  Ejecuta <code className="rounded bg-amber-100 dark:bg-amber-500/20 px-1">make seed</code> o
                  crea el usuario manualmente.
                </p>
                <button
                  type="button"
                  className="mt-2 underline"
                  onClick={() => firebaseAuth.signOut()}
                >
                  Cerrar sesión
                </button>
              </div>
            )}

            <form onSubmit={onSubmit} className="mt-7 space-y-4">
              <label className="block">
                <span className="text-[0.9375rem] font-medium text-content-muted">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="tu@compañía.mx"
                  className={FIELD}
                />
              </label>

              <label className="block">
                <div className="flex items-baseline justify-between">
                  <span className="text-[0.9375rem] font-medium text-content-muted">Contraseña</span>
                  {/* Next to the field it belongs to, not orphaned below the
                      submit button where it reads as a second action. */}
                  <button
                    type="button"
                    className="rounded text-[0.8125rem] text-content-subtle underline underline-offset-2 hover:text-content-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                    onClick={onReset}
                  >
                    ¿La olvidaste?
                  </button>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className={FIELD}
                />
              </label>

              {error && (
                <p
                  role="alert"
                  className="rounded-xl bg-red-50 dark:bg-red-500/10 px-4 py-2.5 text-[0.9375rem] text-red-700 dark:text-red-300"
                >
                  {error}
                </p>
              )}
              {resetSent && (
                <p
                  role="status"
                  className="rounded-xl bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2.5 text-[0.9375rem] text-emerald-800 dark:text-emerald-300"
                >
                  Te enviamos un correo para restablecer tu contraseña.
                </p>
              )}

              {/* size="lg" already sets text-base (16px), which is the right
                  weight for the primary action — a className override here
                  would lose to it on stylesheet order anyway. */}
              <Button
                type="submit"
                size="lg"
                className="w-full"
                loading={loading || authLoading}
              >
                Entrar
              </Button>
            </form>
          </div>
        </div>

        {/* slate-500, not the lighter slate-400 this would naturally take:
            small text needs 4.5:1 against white to stay legible, and 400 only
            reaches 2.56:1. The heart is decorative, so aria-hidden keeps a
            screen reader from reading "black heart suit" mid-sentence. */}
        <footer className="flex items-center justify-between gap-4 pt-8 text-[0.8125rem] text-content-subtle">
          <span>
            Hecho con{' '}
            <span aria-hidden="true" className="text-rose-500">
              ♥
            </span>
            <span className="sr-only">amor</span> por{' '}
            <a
              href="https://zerep.mx"
              target="_blank"
              rel="noreferrer"
              className="rounded font-medium text-content-muted underline-offset-2 transition-colors hover:text-content hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            >
              zerep.mx
            </a>
          </span>
          <ThemeToggle />
        </footer>
      </section>
    </div>
  );
}
